// Catalog.ID Client — Step-up signing
// ============================================================================
// Sensitive, otherwise token-only actions (account termination, OIDC consent
// approval) require proof that the caller holds the account's main_signing_key —
// not just a session token. This proves key possession: the private key only
// exists in memory after a successful PIN (device unlock) or supersecret unlock,
// so an attacker holding only the session token cannot produce the signature.
//
// Mirrors the rotate-keys challenge flow (auth/key-rotation-view.ts): request a
// single-use challenge from the server, sign "{purpose}\n{cid}\n{challenge}" with
// main_signing_key, and submit the wrapper as the action's challengeSignature.

import * as accountApi from '../api/account';
import { keys } from '../state/keys';
import { session } from '../state/session';
import { getKeyBagEntry } from '../crypto/keybag';
import { signHybrid, wrapperToJson } from '../crypto/signing';
import * as aes from '../crypto/aes';

/**
 * Fetch a step-up challenge for {@link purpose} and sign it with the account's
 * main_signing_key. Returns the signature wrapper JSON to pass as the endpoint's
 * `challengeSignature`. Throws if keys aren't unlocked or there's no active session.
 */
export type StepUpPurpose =
    | 'terminate' | 'oidc-approve' | 'replace-keybag' | 'set-authhash'
    /** Every write on the Authenticator Key surface — store a secret, reveal it,
     *  turn the login gate off (registration-restructure §15.5). `enable` takes a
     *  live code instead: the code is the proof a step-up would not add. */
    | 'totp-manage'
    /** Every SINGLE-ENTRY key-bag write — `POST /api/account/keybag/entries`
     *  and the status `PUT` beside it (cid-encrypted-storage §10 item 7). The
     *  add is an UPSERT (`ON CONFLICT … DO UPDATE`), so a bare session token
     *  was enough to overwrite an existing entry's ciphertext — including
     *  `main_signing_key@0`, which would lock the account out of every
     *  step-up there is. The status PUT is gated for the mirror reason: a
     *  revoke is one keystroke from stranding chain-walked data. The
     *  whole-bag PUT has had its own purpose (`replace-keybag`) since round 8;
     *  this closes the two endpoints beside it. The challenge is single-use,
     *  so a reissue that adds AND revokes signs TWICE. */
    | 'keybag-entry'
    /** Rewriting an EXISTING device row — change PIN/access mode, or reset a
     *  locked one. Both control the release of that browser's device encryption
     *  key, so a session token alone must not be enough. Device *setup* takes no
     *  step-up: it only creates a row holding the caller's own key, and
     *  registration finalize calls it just after the keys were replaced. */
    | 'device-manage'
    /** Irreversible delegate and organization actions (docs/cid-governance.md
     *  M15), signed with the ACTING delegate's own key: removing another delegate,
     *  terminating the organization. A delegate's own termination is `terminate`. */
    | 'delegate-remove' | 'org-terminate';

/** Step-ups an owner signs with the ORGANIZATION's main_signing_key
 *  (docs/cid-governance.md M15): its unlock, the bound organization's unfreeze,
 *  and the retirement of its throwaway password at finalize. The message names
 *  the organization: `"{purpose}\n{orgCid}\n{challenge}"`. */
export type OrgStepUpPurpose = 'org-unlock' | 'org-unfreeze' | 'org-retire-password';

export async function signStepUp(purpose: StepUpPurpose): Promise<string> {
    const bag = keys.getKeyBag();
    if (!bag) {
        throw new Error('Keys are locked — unlock your device before continuing');
    }
    const signingEntry = getKeyBagEntry(bag, 'main_signing_key');
    if (!signingEntry) {
        throw new Error('No main_signing_key available — cannot authorize this action');
    }
    const cid = session.getCid() ?? await restoreSessionCid();

    const { challenge } = await accountApi.getStepUpChallenge(purpose);
    const message = aes.encodeUtf8(`${purpose}\n${cid}\n${challenge}`);
    const wrapper = signHybrid(message, aes.fromBase64(signingEntry.privateKey));
    return wrapperToJson(wrapper);
}

/**
 * Fetch a step-up challenge for {@link purpose} — minted against the owner
 * delegate's session — and sign `"{purpose}\n{orgCid}\n{challenge}"` with the
 * ORGANIZATION's main_signing_key seed (docs/cid-governance.md M15). The caller
 * holds the seed (the registration wizard in memory, a workspace action from the
 * owner's wrapped org key) and zeroes it afterwards.
 */
export async function signOrgStepUp(
    purpose: OrgStepUpPurpose, orgCid: string, orgSigningSeed: Uint8Array,
): Promise<string> {
    const { challenge } = await accountApi.getStepUpChallenge(purpose);
    const message = aes.encodeUtf8(`${purpose}\n${orgCid}\n${challenge}`);
    return wrapperToJson(signHybrid(message, orgSigningSeed));
}

/**
 * After a page reload the session token survives but the session info is
 * restored from /api/account/me only once a device unlock succeeds — and the
 * unlock screens themselves step up (a locked device's reset is
 * 'device-manage'). With a live token, ask the node whose session it is, as
 * app.ts does after an unlock; the node accepts no other CID in the signed
 * message anyway. With no token at all, refuse without asking anything.
 */
async function restoreSessionCid(): Promise<string> {
    if (!session.isActive()) {
        throw new Error('No active session — cannot authorize this action');
    }
    const info = await accountApi.getAccountInfo();
    session.updateInfo({
        cid: info.cid,
        accountType: info.accountType,
        status: info.status,
        isOperator: info.isOperator,
        sessionId: info.sessionId,
    });
    return info.cid;
}
